import {
  DraftComponentType,
  IDraftRecommendation,
} from "../../../store/queries/contractModelDrafts";

/**
 * The review wizard.
 *
 * Ani's v0 mockup splits the review by CLINICAL DOMAIN (contract terms → inpatient → outpatient →
 * base rate files → adjustment rules). Our recommendations are typed by CONSTRUCT (what table the
 * thing becomes). Those two cross-cut: a `case_rate_ruleset` can be inpatient or outpatient, and
 * `lesser_of_charges` is a construct for us but an "adjustment rule" to her.
 *
 * The bridge is the `careSetting` facet on each recommendation. Rate-bearing constructs are routed to
 * the Inpatient or Outpatient step by their care setting; rules that apply across the contract land in
 * Adjustment Rules regardless.
 *
 * The last step is ours, not hers: her mockup ends at a "model complete" screen. Ours ends where the
 * work actually starts — the implementation checklist and the check against production. That is the
 * part that lets someone who is not a payer-contract expert build the thing and know they got it right.
 */

export type StepId =
  | "changes"
  | "documents"
  | "terms"
  | "service_lines"
  | "inpatient"
  | "outpatient"
  | "base_rate_files"
  | "adjustment_rules"
  | "build";

export interface IWizardStep {
  id: StepId;
  title: string;
  blurb: string;
}

export const WIZARD_STEPS: IWizardStep[] = [
  {
    id: "documents",
    title: "Upload Contract",
    blurb: "Upload the payer-contract documents to begin AI extraction.",
  },
  {
    id: "changes",
    title: "What Changed",
    blurb:
      "Everything the last regeneration moved, and nothing it didn't. Shown only once this run has been regenerated.",
  },
  {
    id: "terms",
    title: "Contract Terms",
    blurb: "Effective date, renewal, escalators, and how this payer is routed.",
  },
  {
    id: "service_lines",
    title: "Service Lines",
    blurb:
      "Choose which service lines to model. Anything you skip drops out of the rest of the flow.",
  },
  {
    id: "inpatient",
    title: "Inpatient Rates & Terms",
    blurb: "Inpatient reimbursement rates and methodology.",
  },
  {
    id: "outpatient",
    title: "Outpatient Rates by Service Line",
    blurb: "Outpatient rates and carveouts, grouped by service line.",
  },
  {
    id: "base_rate_files",
    title: "Base Rate Files",
    blurb: "The code-level rate files each rate calculation prices against.",
  },
  {
    id: "adjustment_rules",
    title: "Adjustment Rules",
    blurb:
      "Discounts, caps, bundles, and lesser-of rules derived from the contract.",
  },
  {
    id: "build",
    title: "Build & Verify",
    blurb:
      "Build each confirmed item in Payer Rules, then check it against the database.",
  },
];

/** Constructs that describe money for a specific setting — routed by careSetting. */
const RATE_BEARING: DraftComponentType[] = ["rate_calculation", "carveout"];

/** Constructs that adjust whatever the rate produced. Ani's "Adjustment Rules". */
const ADJUSTMENT_RULES: DraftComponentType[] = [
  "lesser_of_charges",
  "charge_based_pricing",
  "mpdr_ruleset",
  "case_rate_ruleset",
  "package_ruleset",
  "exploding_fee_schedule",
  "unit_cap_ruleset",
];

/** Scoping/routing decisions — no live table, but a human has to act on them. Shown with the terms. */
const ROUTING: DraftComponentType[] = [
  "contract_term",
  "payer_consolidation",
  "ma_payer_link",
  "provider_decomposition",
];

export type CareSetting = "inpatient" | "outpatient" | "both";

const modeling = (rec: IDraftRecommendation): Record<string, unknown> =>
  (rec.proposedModeling ?? {}) as Record<string, unknown>;

export const careSettingOf = (rec: IDraftRecommendation): CareSetting => {
  const value = modeling(rec).careSetting;
  return value === "inpatient" || value === "outpatient" ? value : "both";
};

export const serviceLineOf = (rec: IDraftRecommendation): string | null => {
  const value = modeling(rec).serviceLine;
  return typeof value === "string" && value.trim().length > 0
    ? value.trim()
    : null;
};

/**
 * Which step a recommendation is reviewed in. `inpatient_ruleset` is an inpatient construct by
 * definition, so it goes there regardless of what careSetting the agent put on it.
 */
export const stepForRecommendation = (rec: IDraftRecommendation): StepId => {
  const type = rec.componentType;
  if (ROUTING.includes(type)) return "terms";
  if (type === "base_rate_file") return "base_rate_files";
  if (type === "inpatient_ruleset") return "inpatient";
  if (ADJUSTMENT_RULES.includes(type)) return "adjustment_rules";
  if (RATE_BEARING.includes(type)) {
    return careSettingOf(rec) === "inpatient" ? "inpatient" : "outpatient";
  }
  return "adjustment_rules";
};

/**
 * `superseded` recommendations are tombstones — the agent has withdrawn them. They belong in the
 * Changes view (so a withdrawal she'd accepted, or worse BUILT, is impossible to miss) but they must
 * not clutter the working steps, where they would look like live recommendations awaiting review.
 */
const isTombstone = (r: IDraftRecommendation): boolean =>
  r.changeKind === "superseded" || r.status === "superseded";

export const recommendationsForStep = (
  recs: IDraftRecommendation[],
  step: StepId
): IDraftRecommendation[] => {
  // "changes" is a virtual step: it reads the whole run rather than owning any recommendation.
  if (step === "changes") return [];
  return recs.filter(
    (r) => !isTombstone(r) && stepForRecommendation(r) === step
  );
};

/** Every distinct service line the agent found, in stable order, for the picker. */
export const serviceLinesIn = (recs: IDraftRecommendation[]): string[] => {
  const seen: string[] = [];
  recs.forEach((r) => {
    const line = serviceLineOf(r);
    if (line && !seen.includes(line)) seen.push(line);
  });
  return seen.sort((a, b) => a.localeCompare(b));
};

/**
 * A step is done when every recommendation in it has a verdict. `needs_input` does NOT count —
 * that rec has field errors and cannot be built until a human fixes it.
 */
export const stepIsConfirmed = (
  recs: IDraftRecommendation[],
  step: StepId
): boolean => {
  const inStep = recommendationsForStep(recs, step);
  if (inStep.length === 0) return true;
  return inStep.every(
    (r) =>
      r.status === "accepted" ||
      r.status === "rejected" ||
      r.status === "overridden"
  );
};

/** Recs still awaiting a verdict in this step — what the "n left to review" count shows. */
export const unresolvedInStep = (
  recs: IDraftRecommendation[],
  step: StepId
): number =>
  recommendationsForStep(recs, step).filter(
    (r) => r.status === "pending" || r.status === "needs_input"
  ).length;

/**
 * Whether this step holds the reviewer where they are — the gate behind "Confirm & continue" in Ani's
 * v0, which locks each step until the previous one is settled.
 *
 * **`needs_input` deliberately does NOT block.** It is a designed state: the agent's own numbers failed
 * validation, so the item cannot be built until a human fixes it. Gating on it would let one unusable
 * recommendation freeze the entire run with no way past — the reviewer could neither fix it (the data
 * is wrong at the source) nor move on. It stays loud instead: red in the grid, counted in the rail, and
 * it keeps the step off "confirmed", so it is visible without being a dead end.
 *
 * Only `pending` blocks — an item nobody has looked at yet.
 */
export const stepBlocksAdvance = (
  recs: IDraftRecommendation[],
  step: StepId
): boolean =>
  recommendationsForStep(recs, step).some((r) => r.status === "pending");

/**
 * How far the reviewer may navigate: every step up to and including the first one that still blocks.
 *
 * `reachedIndex` is the high-water mark the caller has already unlocked, and it only ever moves
 * FORWARD. That is what stops a regeneration from throwing someone back to step one — a regeneration
 * resets modified recommendations to `pending`, so an earlier step can start blocking again *after*
 * you have passed it. Without the high-water mark the reviewer would be silently relegated mid-review
 * and lose their place. The Changes screen is what tells them something moved; the lock should not.
 */
export const unlockedThroughIndex = (
  recs: IDraftRecommendation[],
  reachedIndex = 0
): number => {
  const firstBlocking = WIZARD_STEPS.findIndex((s) =>
    stepBlocksAdvance(recs, s.id)
  );
  const gate = firstBlocking === -1 ? WIZARD_STEPS.length - 1 : firstBlocking;
  return Math.max(gate, reachedIndex);
};
